import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ChevronDown, ChevronUp, Circle, Clock, Loader2, RotateCcw, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Mandala from "@/components/Mandala";
import { toast } from "sonner";
import { formatYMD as fmtDate } from "@/lib/dates";
import AdminShell from "@/components/admin/AdminShell";
import ModuleProgressTracker from "@/components/lms/ModuleProgressTracker";

// Per-drill error boundary — keeps the rest of the Progress page usable if
// one student's card throws (bad data, unexpected shape, etc.). This is
// especially important now that Holly's admin panel loads a lot of nested
// LMS data — a single bad legacy record shouldn't blank the whole screen.
class DrillErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("[AdminProgress drill]", err, info);
  }
  render() {
    if (this.state.err) {
      return (
        <div className="p-6 bg-red-50 border border-red-200 rounded-2xl">
          <p className="font-medium text-red-800">Couldn't render this student's details.</p>
          <p className="text-[12px] text-red-700 mt-1 whitespace-pre-wrap">{String(this.state.err?.message || this.state.err)}</p>
          <button
            onClick={() => this.setState({ err: null })}
            className="mt-3 btn-secondary !py-2 !px-4 !text-[11px]"
          >Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const STATUS_ICON = {
  approved:        { Icon: CheckCircle2, color: "text-green-700" },
  submitted:       { Icon: Clock,        color: "text-aviana-gold" },
  needs_revision:  { Icon: RotateCcw,    color: "text-aviana-plum" },
  rejected:        { Icon: XCircle,      color: "text-red-700" },
};

function MilestoneCell({ ms }) {
  // Count-style milestone (hours / evals / self-evals)
  if (typeof ms.value === "number") {
    const pct = Math.min(100, (ms.value / ms.target) * 100);
    const done = ms.done;
    return (
      <div className="text-center" data-testid={`cell-${ms.label.replace(/[^a-z]/gi,"-").toLowerCase()}`}>
        <div className={`font-display text-lg leading-none ${done ? "text-green-700" : "text-aviana-ink"}`}>
          {ms.value}<span className="text-aviana-muted text-[12px]">/{ms.target}</span>
        </div>
        <div className="h-1 rounded-full bg-aviana-border mt-2 overflow-hidden">
          <div className={`h-full ${done ? "bg-green-700" : "bg-aviana-gold"}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }
  // Status-style milestone (research, video, exam)
  if ("status" in ms) {
    if (!ms.status) {
      return <div className="text-center text-aviana-muted/60" title="Not submitted"><Circle size={16} className="inline-block" /></div>;
    }
    const { Icon, color } = STATUS_ICON[ms.status] || STATUS_ICON.submitted;
    return <div className={`text-center ${color}`} title={ms.status.replace("_"," ")}><Icon size={18} className="inline-block" /></div>;
  }
  // Pass/fail (exam)
  return ms.done
    ? <div className="text-center text-green-700" title="Passed"><CheckCircle2 size={18} className="inline-block" /></div>
    : <div className="text-center text-aviana-muted/60" title="Not yet"><Circle size={16} className="inline-block" /></div>;
}

export default function AdminProgress() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // course slug | "all"
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null); // `${user_id}-${course_wp_id}` when expanded
  const [detailCache, setDetailCache] = useState({}); // user_id → {lms_hours, lms_self_evals, lms_student_evals, lms_submissions}
  const [detailLoading, setDetailLoading] = useState(null);

  const toggleRow = async (r) => {
    const key = `${r.user.id}-${r.course.wp_post_id}`;
    if (expanded === key) {
      setExpanded(null);
      return;
    }
    setExpanded(key);
    if (!detailCache[r.user.id]) {
      setDetailLoading(r.user.id);
      try {
        const { data } = await api.get(`/admin/users/${r.user.id}`);
        setDetailCache((prev) => ({ ...prev, [r.user.id]: data }));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load student details");
      } finally {
        setDetailLoading(null);
      }
    }
  };

  const logOfflineSubmission = async (userId, courseWpId) => {
    const module_key = window.prompt(
      `Log an OFFLINE submission (e.g., paper emailed to you).\n\n` +
      `Which module?\n` +
      `  • research_paper\n  • exam\n  • class_audit\n  • practice_teaching_video\n\n` +
      `Type the module key exactly:`,
      "research_paper"
    );
    if (!module_key) return;
    const external_url = window.prompt(
      `Optional link to the submission (Google Doc, Dropbox, YouTube, etc.)\n\n` +
      `Leave blank if you'll paste text instead.`,
      ""
    );
    if (external_url == null) return;
    const text_content = window.prompt(
      `Optional text content (paste the paper text here, or a summary).\n\n` +
      `Leave blank if the link above is enough.`,
      ""
    );
    if (text_content == null) return;
    const approve = window.confirm(
      `Auto-approve this submission and email the student a confirmation?\n\n` +
      `OK = APPROVE now (recommended for emailed papers you've already reviewed)\n` +
      `Cancel = LOG as pending (you'll review it later)`
    );
    const reviewer_notes = approve
      ? window.prompt(`Optional approval note to the student (will appear in the email):`, "") || ""
      : "";
    try {
      await api.post(`/admin/lms/submissions/offline`, {
        user_id: userId,
        course_wp_id: String(courseWpId),
        module_key,
        external_url,
        text_content,
        auto_approve: approve,
        reviewer_notes,
      });
      toast.success(approve ? "Logged & approved — student notified" : "Logged as pending — you can review later");
      // Refetch THIS user's detail immediately so the drill-down updates in
      // place (previously we nuked the whole cache which caused the still-
      // expanded row to show "No records found." until the admin manually
      // toggled it closed and open again).
      try {
        const { data: fresh } = await api.get(`/admin/users/${userId}`);
        setDetailCache((prev) => ({ ...prev, [userId]: fresh }));
      } catch { /* keep whatever's already cached */ }
      const { data } = await api.get("/admin/lms/student-progress");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not log submission");
    }
  };

  const reviewSubmission = async (userId, subId, status, notesPromptDefault = "") => {
    const notes = window.prompt(
      status === "approved"
        ? `Approve this submission?\n\nOptional note to the student (leave blank for a default congrats message):`
        : status === "needs_revision"
        ? `Mark this submission as "Needs revision".\n\nWhat should the student fix? (This message will be emailed to them.)`
        : `Reject this submission.\n\nOptional reason (will be emailed to the student):`,
      notesPromptDefault
    );
    if (notes == null) return;
    try {
      await api.post(`/admin/lms/submissions/${subId}/review`, { status, reviewer_notes: notes });
      toast.success(status === "approved" ? "Approved — student notified" : status === "needs_revision" ? "Marked needs revision — student notified" : "Rejected — student notified");
      // Refresh both the top-level counts AND the currently-expanded student's drill-down,
      // so the submission's status badge updates in place and the action buttons disappear
      // without the admin needing to close & reopen the row.
      const [rowsRes, detailRes] = await Promise.all([
        api.get("/admin/lms/student-progress"),
        api.get(`/admin/users/${userId}`),
      ]);
      setRows(rowsRes.data);
      setDetailCache((prev) => ({ ...prev, [userId]: detailRes.data }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save review");
    }
  };

  // Override a quiz attempt's pass/fail after reviewing essay answers. Holly
  // rarely needs this — auto-grading on the MCQ portion is the primary flow —
  // but it exists so a student who nails the MCQs but blows the essays can be
  // held back, or vice-versa.
  const overrideQuizAttempt = async (userId, attemptId, passed) => {
    const notes = window.prompt(
      passed
        ? "Manually mark this exam attempt as PASSED?\n\nOptional note (internal only):"
        : "Manually mark this exam attempt as FAILED?\n\nOptional note (internal only):",
      ""
    );
    if (notes == null) return;
    try {
      await api.post(`/admin/lms/quiz-attempts/${attemptId}/override`, { passed, notes });
      toast.success(passed ? "Attempt marked as passed" : "Attempt marked as failed");
      const [rowsRes, detailRes] = await Promise.all([
        api.get("/admin/lms/student-progress"),
        api.get(`/admin/users/${userId}`),
      ]);
      setRows(rowsRes.data);
      setDetailCache((prev) => ({ ...prev, [userId]: detailRes.data }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not override attempt");
    }
  };

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (user && user.role !== "admin") { nav("/"); return; }
  }, [user, nav]);

  // Extracted so the CertificationPanel can call it after granting/revoking
  // certification or purging module data — refreshes the whole row list so
  // the "CERTIFIED" summary + row-level cert flags update instantly.
  const load = React.useCallback(async () => {
    if (user?.role !== "admin") return;
    try {
      const { data } = await api.get("/admin/lms/student-progress");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load progress");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const courses = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.course.slug, r.course.title));
    return Array.from(m.entries());
  }, [rows]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && r.course.slug !== filter) return false;
      if (s && !(r.user.email.toLowerCase().includes(s) || (r.user.name || "").toLowerCase().includes(s))) return false;
      return true;
    });
  }, [rows, filter, search]);

  const summary = useMemo(() => {
    const total = filtered.length;
    const certified = filtered.filter((r) => Object.values(r.milestones).every((m) => m.done)).length;
    const stuck = filtered.filter((r) => Object.values(r.milestones).filter((m) => m.done).length === 0).length;
    return { total, certified, stuck, inProgress: total - certified - stuck };
  }, [filtered]);

  // Count submissions across visible rows that are in "submitted" state and
  // awaiting Holly's review. Includes research + video milestones which are
  // the two graded-submission types tracked on Cohort Progress.
  const pendingSummary = useMemo(() => {
    let subs = 0;
    const studentsSet = new Set();
    for (const r of filtered) {
      let rowHas = false;
      if (r.milestones.research?.status === "submitted") { subs++; rowHas = true; }
      if (r.milestones.video?.status === "submitted") { subs++; rowHas = true; }
      if (rowHas) studentsSet.add(r.user.id);
    }
    return { subs, students: studentsSet.size };
  }, [filtered]);


  if (!user || user.role !== "admin") return null;

  return (
    <AdminShell>
      <div className="max-w-6xl" data-testid="admin-progress-page">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="overline-plum mb-3">Certification tracking</p>
            <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">Student progress</h1>
            <p className="mt-3 text-aviana-muted max-w-xl">Every enrolled student, every milestone, in one view. Spot who's close to certification and who needs a nudge.</p>
          </div>
          <Mandala size={72} variant="star" className="text-aviana-gold opacity-60" />
        </div>

        {/* Ready-for-review banner — only appears when there's work waiting */}
        {pendingSummary.subs > 0 && (
          <div
            className="mb-6 flex items-center justify-between gap-4 bg-aviana-plum/10 border-2 border-aviana-plum/40 rounded-2xl px-6 py-4"
            data-testid="ready-for-review-banner"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔔</span>
              <div>
                <p className="font-display text-lg text-aviana-plum leading-tight">
                  <strong>{pendingSummary.subs}</strong> submission{pendingSummary.subs === 1 ? "" : "s"} across{" "}
                  <strong>{pendingSummary.students}</strong> student{pendingSummary.students === 1 ? "" : "s"} awaiting your review
                </p>
                <p className="text-[12px] text-aviana-muted mt-0.5">Click any student row below with a clock icon to review + approve.</p>
              </div>
            </div>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-7">
          <div className="bg-aviana-cream rounded-2xl p-5 border border-aviana-border" data-testid="summary-total">
            <p className="overline-plum text-[10px]">Enrollments</p>
            <p className="font-display text-3xl text-aviana-ink mt-2">{summary.total}</p>
          </div>
          <div className="bg-aviana-cream rounded-2xl p-5 border border-green-700/30" data-testid="summary-certified">
            <p className="overline text-[10px] text-green-700">Certified</p>
            <p className="font-display text-3xl text-green-700 mt-2">{summary.certified}</p>
          </div>
          <div className="bg-aviana-cream rounded-2xl p-5 border border-aviana-gold/40" data-testid="summary-in-progress">
            <p className="overline text-[10px] text-aviana-gold">In progress</p>
            <p className="font-display text-3xl text-aviana-ink mt-2">{summary.inProgress}</p>
          </div>
          <div className="bg-aviana-cream rounded-2xl p-5 border border-aviana-plum/30" data-testid="summary-stuck">
            <p className="overline text-[10px] text-aviana-plum">Not yet started</p>
            <p className="font-display text-3xl text-aviana-plum mt-2">{summary.stuck}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-5 flex-wrap items-center" data-testid="filters">
          <button onClick={() => setFilter("all")} className={`px-4 py-2 rounded-full text-[11px] tracking-[0.14em] uppercase border ${filter === "all" ? "bg-aviana-ink text-aviana-cream border-aviana-ink" : "border-aviana-border text-aviana-muted hover:text-aviana-ink"}`} data-testid="filter-all">
            All courses
          </button>
          {courses.map(([slug, title]) => (
            <button
              key={slug}
              onClick={() => setFilter(slug)}
              className={`px-4 py-2 rounded-full text-[11px] tracking-[0.14em] uppercase border ${filter === slug ? "bg-aviana-ink text-aviana-cream border-aviana-ink" : "border-aviana-border text-aviana-muted hover:text-aviana-ink"}`}
              data-testid={`filter-${slug}`}
            >
              {title.replace(/Restorative Online Teacher Training Course/i,"Restorative")
                    .replace(/Aerial Yoga Teacher Training/i,"Aerial YTT")}
            </button>
          ))}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="ml-auto bg-aviana-cream border border-aviana-border rounded-full px-5 py-2 text-[13px] outline-none focus:border-aviana-gold/60 w-72"
            data-testid="search-input"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="animate-spin text-aviana-gold mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-aviana-cream rounded-2xl p-12 border border-aviana-border text-center">
            <Mandala size={64} variant="lotus" className="text-aviana-gold mx-auto opacity-60 mb-5" />
            <p className="font-display text-xl text-aviana-ink">No enrollments match.</p>
          </div>
        ) : (
          <div className="bg-aviana-cream rounded-2xl border border-aviana-border overflow-hidden" data-testid="progress-table">
            <div className="grid grid-cols-[minmax(220px,1fr)_repeat(6,90px)_70px] gap-3 px-5 py-3 border-b border-aviana-border bg-aviana-surface text-[10px] tracking-[0.18em] uppercase text-aviana-muted">
              <div>Student / Course</div>
              <div className="text-center">Exam</div>
              <div className="text-center">Research</div>
              <div className="text-center">Hours</div>
              <div className="text-center">Student evals</div>
              <div className="text-center">Self-evals</div>
              <div className="text-center">Video</div>
              <div className="text-center">Progress</div>
            </div>
            {filtered.map((r) => {
              const done = Object.values(r.milestones).filter((m) => m.done).length;
              const fullyCertified = done === 6;
              const key = `${r.user.id}-${r.course.wp_post_id}`;
              const isOpen = expanded === key;
              const detail = detailCache[r.user.id];
              return (
                <React.Fragment key={key}>
                <div
                  className={`grid grid-cols-[minmax(220px,1fr)_repeat(6,90px)_70px] gap-3 px-5 py-4 border-b border-aviana-border/60 last:border-0 items-center cursor-pointer hover:bg-aviana-surface/40 transition-colors ${fullyCertified ? "bg-green-700/5" : ""}`}
                  data-testid={`row-${r.user.id}-${r.course.wp_post_id}`}
                  onClick={() => toggleRow(r)}
                  title="Click to view all submissions, hours, and evaluations"
                >
                  <div className="min-w-0 flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-[15px] text-aviana-ink truncate flex items-center gap-1.5">
                        {r.user.name || r.user.email}
                        {isOpen ? <ChevronUp size={14} className="text-aviana-plum shrink-0" /> : <ChevronDown size={14} className="text-aviana-muted shrink-0" />}
                      </p>
                      <p className="text-[11px] text-aviana-muted truncate">{r.user.email}</p>
                      <p className="text-[10px] tracking-[0.15em] uppercase text-aviana-gold/80 mt-1 truncate">{r.course.title}</p>
                    </div>
                  </div>
                  <MilestoneCell ms={r.milestones.exam} />
                  <MilestoneCell ms={r.milestones.research} />
                  <MilestoneCell ms={r.milestones.hours} />
                  <MilestoneCell ms={r.milestones.student_evals} />
                  <MilestoneCell ms={r.milestones.self_evals} />
                  <MilestoneCell ms={r.milestones.video} />
                  <div className="text-center">
                    <span className={`inline-block px-3 py-1 rounded-full text-[11px] font-semibold ${fullyCertified ? "bg-green-700 text-white" : "bg-aviana-gold/10 text-aviana-ink"}`}>
                      {r.module_progress ? `${r.module_progress.percent}%` : `${done}/6`}
                    </span>
                    {r.module_progress && (
                      <p className="text-[10px] text-aviana-muted mt-1" data-testid={`row-modules-${r.user.id}-${r.course.wp_post_id}`}>
                        {r.module_progress.complete}/{r.module_progress.total} modules
                      </p>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div className="border-b border-aviana-border/60 bg-aviana-paper/40 px-5 py-6" data-testid={`drill-${r.user.id}`}>
                    {r.module_progress && (
                      <div className="mb-6 bg-white rounded-2xl border border-aviana-border px-6 py-5" data-testid={`admin-module-progress-${r.user.id}-${r.course.wp_post_id}`}>
                        <p className="overline-plum mb-3">Module progress · {r.course.title}</p>
                        <ModuleProgressTracker progress={r.module_progress} testid={`admin-modules-${r.user.id}-${r.course.wp_post_id}`} />
                      </div>
                    )}
                    {detailLoading === r.user.id ? (
                      <p className="text-aviana-muted text-[13px]"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading student records…</p>
                    ) : !detail ? (
                      <p className="text-aviana-muted text-[13px] italic">No records found.</p>
                    ) : (
                      <DrillErrorBoundary>
                        <DrillContent
                          detail={detail}
                          courseWpId={r.course.wp_post_id}
                          row={r}
                          onReview={(subId, status, notes) => reviewSubmission(r.user.id, subId, status, notes)}
                          onLogOffline={() => logOfflineSubmission(r.user.id, r.course.wp_post_id)}
                          onOverrideQuiz={(attemptId, passed) => overrideQuizAttempt(r.user.id, attemptId, passed)}
                          onCertChanged={load}
                        />
                      </DrillErrorBoundary>
                    )}
                  </div>
                )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-5 text-[11px] text-aviana-muted">
          <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-green-700" /> Approved / complete</span>
          <span className="flex items-center gap-1.5"><Clock size={14} className="text-aviana-gold" /> Awaiting review</span>
          <span className="flex items-center gap-1.5"><RotateCcw size={14} className="text-aviana-plum" /> Needs revision</span>
          <span className="flex items-center gap-1.5"><XCircle size={14} className="text-red-700" /> Not approved</span>
          <span className="flex items-center gap-1.5"><Circle size={14} className="text-aviana-muted/60" /> Not submitted</span>
          <span className="flex items-center gap-1.5 ml-auto text-aviana-plum italic">💡 Click any student row to view records + grade submissions</span>
        </div>
      </div>
    </AdminShell>
  );
}

// ─── Inline drill-down for a single student's cohort-progress row ───────────
// Shows the actual records (hours, self-evals, student-evals received, and
// module submissions) filtered to the course of the clicked row. Grade actions
// (Approve / Needs revision / Reject with an optional note) fire directly
// against `/admin/lms/submissions/{id}/review` — no navigation required.
function DrillContent({ detail, courseWpId, row, onReview, onLogOffline, onOverrideQuiz, onCertChanged }) {
  const hours = detail?.lms_hours || { items: [], total_hours: 0 };
  // Self-evals and student-evals aren't scoped to a course_wp_id in the schema
  // (they're per-user). Show ALL of them for this student. Only submissions
  // carry course_wp_id, so filter those.
  const selfEvals = detail?.lms_self_evals || [];
  const studentEvals = detail?.lms_student_evals || [];
  const submissions = (detail?.lms_submissions || []).filter((s) => !courseWpId || String(s.course_wp_id) === String(courseWpId));
  const quizAttempts = (detail?.lms_quiz_attempts || []).filter((a) => !courseWpId || String(a.course_wp_id) === String(courseWpId));
  // Locate the enrollment record for THIS course so the certification panel
  // has an ID to POST against + status to display. Prefer the enrollment_id
  // that the student-progress endpoint returns directly on the row (fast +
  // guaranteed for this specific course), fall back to scanning items.
  const enrollment = row?.enrollment_id
    ? {
        id: row.enrollment_id,
        extra: {
          certification_granted: row.certification_granted,
          certification_granted_at: row.certification_granted_at,
          certification_notes: row.certification_notes,
          module_data_purged_at: row.module_data_purged_at,
          course_wp_id: courseWpId,
        },
      }
    : (detail?.items || []).find(
        (it) => it.kind === "enrollment" && String(it?.extra?.course_wp_id) === String(courseWpId)
      );
  return (
    <div className="space-y-4">
      <CertificationPanel enrollment={enrollment} row={row} onCertChanged={onCertChanged} />
      <Panel title="Practice Teaching Hours" count={`${hours.total_hours}h · ${hours.items.length} session${hours.items.length === 1 ? "" : "s"}`}>
        {hours.items.length === 0 ? (
          <p className="text-aviana-muted text-[13px] italic">No hours logged yet.</p>
        ) : (
          <ul className="space-y-2">
            {hours.items.map((h) => (
              <li key={h.id} className="flex items-baseline gap-3 py-2 px-3 bg-white rounded-lg border border-aviana-border/60 text-[13px]">
                <span className="font-display text-lg text-aviana-gold min-w-[54px]">{h.duration_minutes}m</span>
                <div className="flex-1">
                  <p className="text-aviana-ink">{fmtDate(h.date)} · {h.location || "—"} · {h.student_count || 1} student{h.student_count === 1 ? "" : "s"}</p>
                  {h.notes && <p className="text-aviana-muted text-[12px] italic mt-0.5">"{h.notes}"</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Self-Evaluations" count={`${selfEvals.length} submitted`}>
        {selfEvals.length === 0 ? (
          <p className="text-aviana-muted text-[13px] italic">No self-evals submitted yet.</p>
        ) : (
          <div className="space-y-3">
            {selfEvals.map((s) => (
              <div key={s.id} className="py-4 px-5 bg-white rounded-lg border border-aviana-border/60 text-[13px]">
                <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
                  <p className="text-aviana-ink font-medium">{fmtDate(s.class_date) || "Class date not recorded"}</p>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-aviana-muted">Submitted {fmtDate(s.submitted_at)}</p>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-aviana-inkSoft mb-3 bg-aviana-surface/60 rounded p-3">
                  {s.type_of_class && <p><span className="text-aviana-muted">Class type:</span> {s.type_of_class}</p>}
                  {s.style_focus && <p><span className="text-aviana-muted">Style focus:</span> {s.style_focus}</p>}
                  {s.number_of_students != null && <p><span className="text-aviana-muted">Students:</span> {s.number_of_students}</p>}
                  {s.essential_oils && <p><span className="text-aviana-muted">Essential oils:</span> {s.essential_oils}</p>}
                </div>
                <EvalAnswers pretty={s.answers_pretty} raw={s.answers} />
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Student Evaluations Received" count={`${studentEvals.length} student${studentEvals.length === 1 ? "" : "s"} rated`}>
        {studentEvals.length === 0 ? (
          <p className="text-aviana-muted text-[13px] italic">No student evaluations received yet.</p>
        ) : (
          <div className="space-y-3">
            {studentEvals.map((e) => (
              <div key={e.id} className="py-4 px-5 bg-white rounded-lg border border-aviana-border/60 text-[13px]">
                <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                  <p className="text-aviana-ink">
                    <strong>{e.student_name || "Anonymous student"}</strong>
                    {e.student_email && <span className="text-aviana-muted ml-2">&lt;{e.student_email}&gt;</span>}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-aviana-muted">{fmtDate(e.submitted_at)}</p>
                </div>
                <div className="grid grid-cols-2 gap-x-4 text-[12px] text-aviana-inkSoft mb-3 bg-aviana-surface/60 rounded p-3">
                  {e.class_date && <p><span className="text-aviana-muted">Class date:</span> {fmtDate(e.class_date)}</p>}
                  {e.location && <p><span className="text-aviana-muted">Location:</span> {e.location}</p>}
                </div>
                {e.rating != null && <p className="text-aviana-goldDeep text-[13px] mb-1">Rating: <strong>{e.rating}/5</strong></p>}
                {e.comments && <p className="text-aviana-inkSoft whitespace-pre-wrap mb-3 italic border-l-2 border-aviana-gold/40 pl-3">"{e.comments}"</p>}
                <EvalAnswers pretty={e.answers_pretty} raw={e.answers} />
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Module Submissions (Research paper · Exam · Class audit · Video)" count={
        submissions.length === 0
          ? "None yet"
          : `${submissions.filter((s) => s.status === "submitted").length} awaiting review · ${submissions.filter((s) => s.status === "approved").length} approved`
      }>
        {submissions.length === 0 ? (
          <p className="text-aviana-muted text-[13px] italic">No submissions yet.</p>
        ) : (
          <div className="space-y-3">
            {submissions.map((s) => (
              <div key={s.id} className="py-3 px-4 bg-white rounded-lg border border-aviana-border/60 text-[13px]" data-testid={`sub-${s.id}`}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1.5">
                  <p className="text-aviana-ink">
                    <strong>{(s.module_key || "submission").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</strong>
                    <span className={`ml-2 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full ${
                      s.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                      s.status === "needs_revision" ? "bg-amber-100 text-amber-800" :
                      s.status === "rejected" ? "bg-red-100 text-red-700" :
                      "bg-aviana-plum/15 text-aviana-plum"
                    }`}>{s.status || "submitted"}</span>
                    {s.submitted_offline && (
                      <span className="ml-1.5 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-aviana-gold/15 text-aviana-goldDeep">Offline</span>
                    )}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-aviana-muted">{fmtDate(s.submitted_at)}</p>
                </div>
                {s.external_url && <p className="text-[12px]"><a href={s.external_url} target="_blank" rel="noreferrer" className="text-aviana-plum underline break-all">{s.external_url}</a></p>}
                {s.file_url && <p className="text-[12px]"><a href={s.file_url} target="_blank" rel="noreferrer" className="text-aviana-plum underline break-all">Attached file</a></p>}
                {s.text_content && <p className="text-aviana-inkSoft whitespace-pre-wrap mt-1.5 max-h-64 overflow-y-auto pr-2">{s.text_content}</p>}
                {s.reviewer_notes && <p className="text-[12px] text-aviana-goldDeep mt-1.5 border-l-2 border-aviana-gold pl-2 italic">Your note: {s.reviewer_notes}</p>}
                {s.status !== "approved" && (
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-aviana-border/60">
                    <button onClick={() => onReview(s.id, "approved")} className="btn-secondary !py-1.5 !px-3 !text-[10px] !bg-green-700 !text-white hover:!bg-green-800" data-testid={`approve-${s.id}`}>✓ Approve</button>
                    <button onClick={() => onReview(s.id, "needs_revision", s.reviewer_notes || "")} className="btn-secondary !py-1.5 !px-3 !text-[10px] !text-aviana-plum" data-testid={`revise-${s.id}`}>↻ Needs revision</button>
                    <button onClick={() => onReview(s.id, "rejected")} className="btn-secondary !py-1.5 !px-3 !text-[10px] !text-red-700" data-testid={`reject-${s.id}`}>✕ Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {onLogOffline && (
          <div className="mt-4 pt-3 border-t border-aviana-border/40">
            <button
              onClick={onLogOffline}
              className="btn-secondary !py-1.5 !px-3 !text-[10px] !text-aviana-goldDeep hover:!bg-aviana-gold/10"
              data-testid="log-offline-submission"
              title="Log a submission the student emailed to you directly (bypasses the student portal)"
            >
              + Log offline submission (emailed paper, etc.)
            </button>
          </div>
        )}
      </Panel>

      <Panel title="Quiz Attempts (Module 1 Exam · Research Paper Quiz)" count={
        quizAttempts.length === 0
          ? "None yet"
          : `${quizAttempts.filter((a) => a.passed).length} passed · ${quizAttempts.length} total`
      }>
        {quizAttempts.length === 0 ? (
          <p className="text-aviana-muted text-[13px] italic">No quiz attempts yet.</p>
        ) : (
          <div className="space-y-3">
            {quizAttempts.map((a) => (
              <div key={a.id} className="py-3 px-4 bg-white rounded-lg border border-aviana-border/60 text-[13px]" data-testid={`attempt-${a.id}`}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1.5">
                  <p className="text-aviana-ink">
                    <strong>{a.quiz_title || "Quiz"}</strong>
                    <span className={`ml-2 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full ${
                      a.passed ? "bg-emerald-100 text-emerald-800" :
                      a.needs_grading ? "bg-amber-100 text-amber-800" :
                      "bg-red-100 text-red-700"
                    }`}>{a.passed ? "Passed" : a.needs_grading ? "Needs review" : "Failed"}</span>
                    {a.manually_overridden && (
                      <span className="ml-1.5 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-aviana-plum/15 text-aviana-plum">Override</span>
                    )}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-aviana-muted">{fmtDate(a.submitted_at)}</p>
                </div>
                <p className="text-[12px] text-aviana-inkSoft mb-1">
                  Score: <strong>{a.earned}/{a.total_points}</strong> ({a.score_pct}%) — pass threshold 75%
                </p>
                {a.reviewer_notes && <p className="text-[12px] text-aviana-goldDeep border-l-2 border-aviana-gold pl-2 italic mt-1.5">Your note: {a.reviewer_notes}</p>}
                {/* MCQ breakdown — show which questions the student got wrong,
                    the answer they picked, and what the correct answer was.
                    Lets Holly send targeted feedback about specific misses. */}
                <MissedMcqBreakdown
                  attemptId={a.id}
                  breakdown={a.breakdown}
                  savedFeedback={a.question_feedback}
                />
                {Array.isArray(a.essay_answers) && a.essay_answers.length > 0 && (
                  <details className="mt-2 pt-2 border-t border-aviana-border/40">
                    <summary className="cursor-pointer text-[12px] text-aviana-plum">Read {a.essay_answers.length} essay answer{a.essay_answers.length === 1 ? "" : "s"} →</summary>
                    <ul className="space-y-3 mt-3">
                      {a.essay_answers.map((e, i) => (
                        <li key={e.question_id}>
                          <p className="text-aviana-ink font-medium text-[12px]" dangerouslySetInnerHTML={{ __html: e.question_html || e.question_title || `Essay ${i + 1}` }} />
                          <p className="text-aviana-inkSoft whitespace-pre-wrap mt-1 text-[12px] bg-aviana-surface p-2 rounded">{e.answer || <em className="text-aviana-muted">(blank)</em>}</p>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {onOverrideQuiz && (
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-aviana-border/60">
                    {!a.passed && (
                      <button onClick={() => onOverrideQuiz(a.id, true)} className="btn-secondary !py-1.5 !px-3 !text-[10px] !bg-green-700 !text-white hover:!bg-green-800" data-testid={`override-pass-${a.id}`}>✓ Mark passed</button>
                    )}
                    {a.passed && (
                      <button onClick={() => onOverrideQuiz(a.id, false)} className="btn-secondary !py-1.5 !px-3 !text-[10px] !text-red-700" data-testid={`override-fail-${a.id}`}>✕ Mark failed</button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Panel({ title, count, children }) {
  return (
    <div className="bg-aviana-cream border border-aviana-border rounded-2xl overflow-hidden">
      <div className="px-5 py-3 border-b border-aviana-border/60 flex items-baseline justify-between gap-3 flex-wrap">
        <p className="text-aviana-ink font-medium text-[13px]">{title}</p>
        <p className="text-aviana-muted text-[12px]">{count}</p>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

// Extracted so the <details><summary> DOM stays canonical (avoids the
// summary-inside-Fragment shape which some browsers won't recognise as a
// valid disclosure widget).
function MissedMcqBreakdown({ attemptId, breakdown, savedFeedback }) {
  const [feedback, setFeedback] = useState(() => ({ ...(savedFeedback || {}) }));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  if (!Array.isArray(breakdown)) return null;
  const missed = breakdown.filter((b) => b && b.is_correct === false);
  if (missed.length === 0) return null;

  const updateFeedback = (qid, val) => {
    setFeedback((f) => ({ ...f, [qid]: val }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/lms/quiz-attempts/${attemptId}/question-feedback`, { feedback });
      toast.success("Feedback saved — the student will see it on their attempt.");
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <details className="mt-2 pt-2 border-t border-aviana-border/40" open>
      <summary className="cursor-pointer text-[12px] text-red-700 font-medium">
        {missed.length} missed question{missed.length === 1 ? "" : "s"} · click to leave feedback →
      </summary>
      <ul className="space-y-3 mt-3">
        {missed.map((b, i) => {
          const labels = Array.isArray(b.choice_labels) ? b.choice_labels : [];
          let picked = "—";
          if (typeof b.submitted === "number") {
            picked = labels[b.submitted] || `Choice ${b.submitted + 1}`;
          } else if (Array.isArray(b.submitted)) {
            picked = b.submitted.map((v) => labels[v] || `Choice ${v + 1}`).join(", ");
          }
          const correct = (Array.isArray(b.correct_idx) ? b.correct_idx : [])
            .map((v) => labels[v])
            .filter(Boolean)
            .join(", ");
          const qid = b.question_id || `q-${i}`;
          return (
            <li key={qid} className="bg-red-50/60 border border-red-100 rounded p-3">
              <p
                className="text-aviana-ink font-medium text-[12px]"
                dangerouslySetInnerHTML={{
                  __html: String(b.question_html || b.question_title || `Question ${i + 1}`),
                }}
              />
              <p className="text-[12px] mt-2">
                <span className="text-red-700 font-medium">Student picked:</span>{" "}
                <span className="text-aviana-ink">{picked || "(no answer)"}</span>
              </p>
              <p className="text-[12px] mt-1">
                <span className="text-emerald-800 font-medium">Correct answer:</span>{" "}
                <span className="text-aviana-ink">{correct || "(not configured)"}</span>
              </p>
              <label className="block mt-3">
                <span className="text-[10px] tracking-[0.16em] uppercase text-aviana-plum">Optional feedback for the student</span>
                <textarea
                  value={feedback[qid] || ""}
                  onChange={(e) => updateFeedback(qid, e.target.value)}
                  rows={2}
                  placeholder="e.g. The correct answer is X because…"
                  className="mt-1 w-full bg-white border border-aviana-border rounded-lg px-3 py-2 text-[12px] outline-none focus:border-aviana-gold/60"
                  data-testid={`missed-feedback-${qid}`}
                />
              </label>
            </li>
          );
        })}
      </ul>
      {dirty && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="btn-gold !py-2 !px-4 !text-[11px]"
            data-testid={`missed-feedback-save-${attemptId}`}
          >
            {saving ? "Saving…" : "Save feedback to student"}
          </button>
        </div>
      )}
    </details>
  );
}

// Grant / revoke certification + purge stored module data once the student
// is fully certified. Purge is gated behind granted=true on the backend, so
// Holly can't accidentally wipe module submissions before certifying.
function CertificationPanel({ enrollment, row, onCertChanged }) {
  const [busy, setBusy] = useState(false);
  if (!enrollment) return null;
  const granted = !!enrollment?.extra?.certification_granted;
  const grantedAt = enrollment?.extra?.certification_granted_at;
  const purgedAt = enrollment?.extra?.module_data_purged_at;
  const enrollmentId = enrollment.id;
  const doneCount = Object.values(row?.milestones || {}).filter((m) => m.done).length;
  const fullyDone = doneCount === 6;

  const toggleCert = async () => {
    const willGrant = !granted;
    if (willGrant && !fullyDone) {
      if (!window.confirm(`Only ${doneCount} of 6 milestones are complete. Grant certification anyway?`)) return;
    }
    const notes = willGrant ? (window.prompt("Optional certification notes (blank = skip)", "") || "") : "";
    setBusy(true);
    try {
      await api.post(`/admin/lms/enrollments/${enrollmentId}/certify`, { granted: willGrant, notes });
      toast.success(willGrant ? "Certification granted" : "Certification revoked");
      onCertChanged && onCertChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const purge = async () => {
    if (!granted) {
      toast.error("Grant certification before purging module data.");
      return;
    }
    if (!window.confirm(
      "Permanently delete this student's exam attempts, submissions, teaching hours, self-evals and student-evals?\n\n" +
      "The certification record itself is preserved. This cannot be undone."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/lms/enrollments/${enrollmentId}/module-data`);
      const c = data?.counts || {};
      toast.success(`Purged ${Object.values(c).reduce((a, b) => a + b, 0)} record(s)`);
      onCertChanged && onCertChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Purge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-2xl border p-5 ${granted ? "bg-green-700/5 border-green-700/30" : "bg-aviana-cream border-aviana-border"}`} data-testid="certification-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="overline-plum mb-1">Certification</p>
          <p className="font-display text-2xl text-aviana-ink leading-tight">
            {granted ? "Certification granted" : "Not yet certified"}
          </p>
          {granted && grantedAt && (
            <p className="text-[12px] text-aviana-muted mt-1">Granted {fmtDate(grantedAt)}</p>
          )}
          {enrollment?.extra?.certification_notes && (
            <p className="text-[12px] text-aviana-inkSoft italic mt-1 max-w-xl">"{enrollment.extra.certification_notes}"</p>
          )}
          {purgedAt && (
            <p className="text-[11px] text-aviana-goldDeep mt-2">Module data purged {fmtDate(purgedAt)}</p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={toggleCert}
            disabled={busy}
            className={granted ? "btn-secondary !py-2 !px-4 !text-[11px]" : "btn-gold !py-2 !px-4 !text-[11px]"}
            data-testid="cert-grant-toggle"
          >
            {busy ? "Saving…" : granted ? "Revoke certification" : "Grant certification"}
          </button>
          {granted && !purgedAt && (
            <button
              onClick={purge}
              disabled={busy}
              className="!py-2 !px-4 !text-[11px] rounded-full border border-red-400 text-red-700 hover:bg-red-50 transition-colors"
              data-testid="cert-purge-modules"
            >
              Delete module data
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Renders evaluation answers legibly — uses the new `answers_pretty` array
// from the backend (question text + labeled answer). Falls back to raw dict
// if the backend hasn't been redeployed yet.
function EvalAnswers({ pretty, raw }) {
  const rows = Array.isArray(pretty) && pretty.length
    ? pretty
    : (raw && typeof raw === "object" && !Array.isArray(raw))
      ? Object.entries(raw).map(([qid, val]) => ({ question_id: qid, question: qid, answer: String(val ?? "—") }))
      : [];
  if (!rows.length) {
    return <p className="text-aviana-muted text-[12px] italic">No answers recorded.</p>;
  }
  return (
    <ul className="space-y-3 pt-2 border-t border-aviana-border/40">
      {rows.map((a, i) => {
        const isRating = a.is_rating;
        const answer = a.answer ?? a.a ?? "—";
        return (
          <li key={a.question_id || i} className={isRating ? "flex items-start justify-between gap-3" : ""}>
            <div className={isRating ? "flex-1" : ""}>
              <p className="text-aviana-ink font-medium text-[13px]">
                {a.question || `Question ${i + 1}`}
              </p>
              {!isRating && (
                <p className="text-aviana-inkSoft whitespace-pre-wrap mt-1 text-[13px] leading-[1.65]">
                  {answer}
                </p>
              )}
            </div>
            {isRating && (
              <span className="inline-flex items-center gap-1.5 text-[12px] font-medium bg-aviana-gold/15 text-aviana-plumDeep px-3 py-1 rounded-full whitespace-nowrap">
                {answer}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
